import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css, spacing } from '@coursera/coursera-ui';

import _t from 'i18n!nls/enterprise-admin-home';

const LEARN_HOW = 'https://business.coursera.help/hc/articles/000002055';
const TEAMS_HELP = 'https://business.coursera.help/hc/articles/000002059';

const styles = StyleSheet.create({
  faqFont: {
    fontSize: '16px',
  },
});

const EXPIRATION_REASON_TAGS = {
  MISSED_PAYMENT: 'MISSING_PAYMENT',
  ENDED: 'CONTRACT_END_DATE',
  REFUNDED: 'REFUNDED',
  MIGRATED: 'MIGRATED',
  UNKNOWN: '',
} as const;

export function getExpirationHeader(expirationReason: string, isFreeTrial = false) {
  if (expirationReason === EXPIRATION_REASON_TAGS.MISSED_PAYMENT) {
    return _t("Your Coursera for Business contract isn't available.");
  } else if (expirationReason === EXPIRATION_REASON_TAGS.REFUNDED) {
    return _t('Your Coursera for Business contract was refunded.');
  } else if (isFreeTrial) {
    return _t('Your Coursera for Business free trial has expired.');
  } else {
    return _t('Your Coursera for Business contract has expired.');
  }
}

export function getExpirationDescription(expirationReason: string, expirationDate: string, isFreeTrial = false) {
  const props = {
    expirationDate: <strong>{expirationDate}</strong>,
    helpCenterLink: (
      <a href={TEAMS_HELP} target="_blank" rel="noopener noreferrer">
        {_t('Team Help Center')}
      </a>
    ),
  };
  if (expirationReason === EXPIRATION_REASON_TAGS.MISSED_PAYMENT) {
    return (
      <FormattedMessage
        message={_t(
          'Your contract was cancelled on {expirationDate} due to missed payments. Learners can still access courses and certificates with their personal account. Questions? Visit the {helpCenterLink}.'
        )}
        {...props}
      />
    );
  } else if (expirationReason === EXPIRATION_REASON_TAGS.REFUNDED) {
    return (
      <FormattedMessage
        message={_t(
          'Your contract was refunded on {expirationDate}. Learners can still access courses and certificates with their personal account. Questions? Visit the {helpCenterLink}.'
        )}
        {...props}
      />
    );
  } else if (isFreeTrial) {
    return (
      <FormattedMessage
        message={_t(
          'Your free trial expired on {expirationDate}. Learners can still access courses and certificates with their personal account. Questions? Visit the {helpCenterLink}.'
        )}
        {...props}
      />
    );
  } else {
    return (
      <FormattedMessage
        message={_t(
          'Your contract expired on {expirationDate}. Learners can still access courses and certificates with their personal account. Questions? Visit the {helpCenterLink}.'
        )}
        {...props}
      />
    );
  }
}

export function expiredFaqs() {
  return [
    {
      id: 'q1',
      question: _t('Why do I need to purchase a new contract?'),
      answer: (
        <div>
          <p {...css(styles.faqFont)}>
            {_t(
              'Your previous contract expired and was canceled. This could have been caused by disabling auto-renew or a failed payment. To continue using Coursera for Business, you’ll need to complete a 3-step process:'
            )}
          </p>
          <p {...css(styles.faqFont)}>
            <ul style={{ marginBottom: '0', paddingLeft: spacing.md }}>
              <li>{_t('Purchase a new contract')}</li>
              <li>{_t('Create a new learning program')}</li>
              <li>{_t('Re-invite learners')}</li>
            </ul>
          </p>
          <p {...css(styles.faqFont)}>
            <FormattedMessage
              message={_t('Questions? Visit the {helpCenterLink}.')}
              helpCenterLink={
                <a href={TEAMS_HELP} target="_blank" rel="noopener noreferrer">
                  {_t('Team Help Center')}
                </a>
              }
            />
          </p>
        </div>
      ),
    },
    {
      id: 'q2',
      question: _t('Can I still purchase the same contract I had before?'),
      answer: (
        <div>
          <p {...css(styles.faqFont)}>
            {_t(
              'With each contract, you can decide how many people you want to include (5 person minimum). You can add more people to your contract after the initial purchase.'
            )}
          </p>
          <p {...css(styles.faqFont)}>
            {_t(
              'Our current contract option includes unlimited courses per person. You get the best value with this flexible contract. Each learner you invite can take an unlimited amount of courses. If a learner is inactive, you can remove them from Coursera for Business and invite a new learner.'
            )}
          </p>
        </div>
      ),
    },
    {
      id: 'q3',
      question: _t('Can I add more people after I purchase the new contract?'),
      answer: (
        <p {...css(styles.faqFont)}>
          <FormattedMessage
            message={_t('You can add more people to your new contract at any time. {LEARN}')}
            LEARN={
              <a href={LEARN_HOW} target="_blank" rel="noopener noreferrer">
                {_t('Learn how')}
              </a>
            }
          />
        </p>
      ),
    },
    {
      id: 'q4',
      question: _t('What happens to the learners who were taking courses through my old contract?'),
      answer: (
        <p {...css(styles.faqFont)}>
          {_t(
            'All learner progress is stored. Their enrollment status automatically switched to audit for any courses in your previous program. Learners can still access courses and certificates with their personal Coursera account.'
          )}
        </p>
      ),
    },
    {
      id: 'q5',
      question: _t('Can people continue in the same courses after I purchase my new contract?'),
      answer: (
        <div>
          <p {...css(styles.faqFont)}>{_t('Yes! After you purchase a new contract, follow these steps:')}</p>
          <p {...css(styles.faqFont)}>
            <ul style={{ marginBottom: '0', paddingLeft: spacing.md }}>
              <li style={{ textAlign: 'left', marginBottom: spacing.md }}>
                <span>
                  {_t(
                    'Create a new learning program. Remember to include the same courses so people can continue learning.'
                  )}
                </span>
              </li>
              <li style={{ textAlign: 'left' }}>
                <span>
                  {_t(
                    'Invite learners to the learning program. You can invite learners who were previously active and new learners.'
                  )}
                </span>
              </li>
            </ul>
          </p>
          <p {...css(styles.faqFont)}>
            {_t(
              'After people accept the invite, they’ll automatically regain full, sponsored access to the course. However, their progress won’t be tracked in program reports.'
            )}
          </p>
        </div>
      ),
    },
    {
      id: 'q6',
      question: _t('How long do people have to complete their courses?'),
      answer: (
        <p {...css(styles.faqFont)}>
          {_t(
            'When someone enrolls in a course from within a program, they get extra time to complete it. They can access the course for the full duration of your contract (12 months). '
          )}
        </p>
      ),
    },
    {
      id: 'q7',
      question: _t('Can I still access reports for the learning programs in my old contract?'),
      answer: (
        <p {...css(styles.faqFont)}>
          {_t(
            'Unfortunately, reports aren’t available for a learning program in an expired contract. We sent an email notification a few weeks before your contract expired to give you a chance to download the reports.'
          )}
        </p>
      ),
    },
  ];
}
